import { useState, useContext, useMemo } from "react";
import {
  Box,
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Slide,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import { Security, AssignmentInd } from "@mui/icons-material";
import { useMutation, useQuery } from "react-query";
import { useSnackbar } from "notistack";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import AuthContext from "../../context/AuthProvider";
import { queryClient } from "../../App";
import DeleteMyAccount from "./deleteAccount";
import { tryRedirectToDeeplinkOrRedirectUri } from "../../utils/profileReturnUrl";

function DeleteProfile() {
  const [searchParams] = useSearchParams();
  const params = useMemo(
    () =>
      new Proxy(searchParams, {
        get: (target, prop) => target.get(String(prop)),
      }),
    [searchParams]
  );
  const [openDeleteAccount, setOpenDeleteAccount] = useState(false);
  const { auth, setAuth, clearAllTenantSessions, clearTenantContext } =
    useContext(AuthContext);
  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { mutate: mutateSignOut } = useMutation((refreshToken) => {
    return axiosPrivate.post(
      "/logout",
      { refresh_token: refreshToken },
      { withCredentials: true }
    );
  });

  const signOutAll = async () => {
    const refreshToken = auth.refresh_token;
    // Preserve tenant_id so the external browser redirects to the correct
    // tenant login page instead of the generic tenant-selection screen.
    const tenantId =
      searchParams.get("tenant_id") || searchParams.get("tenantId");
    const loginTarget = tenantId ? `/login?tenant_id=${tenantId}` : "/login";
    return mutateSignOut(refreshToken, {
      onSuccess: () => {
        clearAllTenantSessions();
        setAuth({});
        try {
          localStorage.setItem("persist", false);
        } catch {}
        clearTenantContext(true);
        queryClient.clear();
        navigate(loginTarget);
      },
      onError: (error) => {
        clearAllTenantSessions();
        setAuth({});
        try {
          localStorage.setItem("persist", false);
        } catch {}
        clearTenantContext(true);
        queryClient.clear();
        navigate(loginTarget);
        console.error({
          error,
          msg: error?.response?.data?.error?.message,
          default: error?.message,
        });
        enqueueSnackbar(
          error?.response?.data?.error?.message ||
            error?.message ||
            "Network Error!",
          {
            variant: "error",
            TransitionComponent: Slide,
            preventDuplicate: true,
            autoHideDuration: 2000,
          }
        );
      },
    });
  };

  const {
    isLoading: fullLoading,
    data: fullAuthData,
  } = useQuery(
    ["authorizedClients", "deleteProfile"],
    () => axiosPrivate.get("oauth/authorizedClients"),
    { refetchOnWindowFocus: false }
  );

  const {
    isLoading: openIdLoading,
    data: openIdData,
  } = useQuery(
    ["openIDAuthorizedClients", "deleteProfile"],
    () => axiosPrivate.get("oauth/openIDAuthorizedClients"),
    { refetchOnWindowFocus: false }
  );

  const isFetching = fullLoading || openIdLoading;
  const fullClients = fullAuthData?.data?.data ?? [];
  const openIdClients = openIdData?.data?.data ?? [];
  const hasAuthorizedApps = fullClients.length + openIdClients.length > 0;

  return isFetching ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        mt: 2,
        minHeight: "100vh",
      }}
    >
      <CircularProgress
        size={40}
        sx={{
          color: "secondary.main",
          marginTop: "-12px",
          marginLeft: "-12px",
        }}
      />
    </Box>
  ) : (
    <>
      <Dialog
        open
        scroll="paper"
        sx={{
          "& .MuiDialog-paper": {
            boxShadow: "0px 1px 1px 1px white",
            maxHeight: "90vh",
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: "white",
          },
        }}
        PaperProps={{
          sx: {
            width: { lg: "30%", md: "45%", xs: "100%", sm: "50%" },
            height: "auto",
          },
        }}
      >
        <DialogTitle sx={{ pb: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", textAlign: "center" }}
          >
            Delete your account
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 1.5, px: 0.5 }}>
            {hasAuthorizedApps
              ? "This permanently removes your account and revokes access to the apps shown below."
              : "This permanently removes your account."}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 2, pb: 0 }}>
          {(fullClients.length > 0 || openIdClients.length > 0) && (
            <Box sx={{ mt: 1 }}>
              {fullClients.length > 0 && (
                <>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
                  >
                    <Security sx={{ color: "primary.main", fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Third-party app access
                    </Typography>
                  </Box>
                  <List dense disablePadding sx={{ mb: openIdClients.length > 0 ? 2 : 0 }}>
                    {fullClients.map((client) => (
                      <ListItem key={client.id} disableGutters sx={{ py: 0.5 }}>
                        <ListItemAvatar sx={{ minWidth: 44 }}>
                          <Avatar
                            alt={client.name}
                            src={client.logo_url}
                            variant="rounded"
                            sx={{ width: 36, height: 36, bgcolor: "grey.100" }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={client.name}
                          secondary={
                            client.auth_given_at
                              ? `Authorized ${moment(client.auth_given_at).format("MMM D, YYYY")}`
                              : null
                          }
                          primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {openIdClients.length > 0 && (
                <>
                  {/* <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                      mt: fullClients.length > 0 ? 1 : 0,
                    }}
                  >
                    <AssignmentInd sx={{ color: "secondary.main", fontSize: 20 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Signing with SSO
                    </Typography>
                  </Box> */}
                  <List dense disablePadding>
                    {openIdClients.map((client) => (
                      <ListItem key={client.id} disableGutters sx={{ py: 0.5 }}>
                        <ListItemAvatar sx={{ minWidth: 44 }}>
                          <Avatar
                            alt={client.name}
                            src={client.logo_url}
                            variant="rounded"
                            sx={{ width: 36, height: 36, bgcolor: "grey.100" }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={client.name}
                          secondary={
                            client.auth_given_at
                              ? `Authorized ${moment(client.auth_given_at).format("MMM D, YYYY")}`
                              : null
                          }
                          primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "stretch",
            gap: 1.5,
            p: 2,
            pt: 1.5,
          }}
        >
          <Button
            fullWidth
            size="large"
            sx={{
              bgcolor: "black",
              ":hover": { bgcolor: "black" },
              textTransform: "none",
              borderRadius: 6,
            }}
            onClick={() => {
              if (
                tryRedirectToDeeplinkOrRedirectUri(
                  params?.deeplink,
                  params?.redirect_uri
                )
              ) {
                return;
              }
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate("/");
              }
            }}
          >
            <Typography sx={{ color: "#fff", fontSize: 14, fontWeight: "bold" }}>
              Back
            </Typography>
          </Button>
          <Button
            fullWidth
            size="large"
            sx={{
              bgcolor: "#e4e4e4",
              ":hover": { bgcolor: "#e4e4e4" },
              textTransform: "none",
              borderRadius: 6,
            }}
            onClick={() => setOpenDeleteAccount(true)}
          >
            <Typography sx={{ color: "black", fontSize: 14, fontWeight: "bold" }}>
              Delete my account
            </Typography>
          </Button>
        </Box>
      </Dialog>
      <DeleteMyAccount
        params={params}
        signOut={signOutAll}
        openDeleteAccount={openDeleteAccount}
        setOpenDeleteAccount={setOpenDeleteAccount}
      />
    </>
  );
}

export default DeleteProfile;
